import Portal from "@Portals/components/Portal";
import BestPriceOnDateRouteList from "@Pages/stationToStation/components/BestPriceOnDateRouteList";
import BestPriceRouteType from "@Pages/stationToStation/types/BestPriceRoute.type";
import React, {useEffect} from "react";
import PortalMap from "@Portals/components/PortalMap";
import DepartureStationPopover
    from "@Pages/stationToStation/features/departureStationPopover/components/DepartureStationPopover";
import StsBusChangeContainer from "@Pages/stationToStation/components/StsBusChangeContainer";
import MTicketPopover from "@Pages/stationToStation/features/mTicketPopover/components/MTicketPopover";
import PricePopover from "@Pages/stationToStation/features/pricePopover/components/PricePopover";
import StationToStationDatePicker from "@Pages/stationToStation/components/StationToStationDatePicker";
import useFerrySupportedLocale from "@GetByFerry/hooks/useFerrySupportedLocale";
import GenericPopover from "@Popover/components/GenericPopover";
import {useTranslation} from "@Translation/hooks/useTranslation";
import {StsCompanyRating} from "@Pages/stationToStation/components/StsCompanyRating";
import {BestPriceTabs} from "@Pages/stationToStation/features/bestPriceTabs/components/BestPriceTabs";
import {TravelBestPricePortal} from "@Pages/stationToStation/components/TravelBestPricePortal";
import IconInfo from "@Icons/IconInfo";
import {Colors} from "@Styles/Colors.style";

interface BestPriceOnDatePortalProps {
    stationToStationHasResults: boolean,
    portToPortHasResults: boolean,
    travelPageHasResults: boolean,
    isTravelPage: boolean,
}

function BestPriceOnDatePortal({stationToStationHasResults, portToPortHasResults, travelPageHasResults, isTravelPage}: BestPriceOnDatePortalProps) {
    const {
        locale,
        transportType,
    } = useFerrySupportedLocale();

    const trigger = () => {
        return (
            <IconInfo fill={Colors.neutralBlackColor}/>
        )
    }

    const overlayContent :string = useTranslation("sts_page.info_button_content");

    // Probably the wrong way to do this,
    // but keeping the logic the same as the original code for the mobile display of the datepicker
    useEffect(() => {
        const panels = document.querySelectorAll<HTMLElement>('.panel');
        const bindings: Array<{ panel: HTMLElement; handler: () => void }> = [];

        panels.forEach((panel) => {
            const handleClick = () => {
                const button = panel.querySelector<HTMLButtonElement>('.btn.btn--blue.sts-date-picker-text');
                if (button && typeof button.click === 'function') {
                    button.click();
                }
            };
            panel.addEventListener('click', handleClick);
            bindings.push({ panel, handler: handleClick });
        });

        // Cleanup event listeners on component unmount
        return () => {
            bindings.forEach(({ panel, handler }) => panel.removeEventListener('click', handler));
        };
    }, []);

    // DEBUG: Uncomment for debugging portal mounting issues
    //  Log all gb--route-datepicker elements
    // useEffect(() => {
    //     console.log('PortalMap: Looking for gb--route-datepicker elements');
    //     const elements = document.querySelectorAll('.gb--route-datepicker');
    //     console.log('Found', elements.length, 'gb--route-datepicker elements');
    //     elements.forEach((el, index) => {
    //         console.log(`Element ${index}:`, el);
    //     });
    // Log data availability
    //     console.log('window.bestPriceOnDateData:', window.bestPriceOnDateData);
    //     console.log('window.bestPriceOnDateDataOverNightTrips:', window.bestPriceOnDateDataOverNightTrips);
    // }, []);

    if (travelPageHasResults) {
        return (
            <>
                <Portal
                    id="travelRoutes"
                    type="div"
                >
                    <TravelBestPricePortal bestPriceOnDateRoutes={window?.bestPriceOnDateDataTravel}/>
                </Portal>
                <Portal id="review-info">
                    <GenericPopover
                        trigger={trigger()}
                        overlay={overlayContent}
                        id="review-info"
                    />
                </Portal>
            </>
        )
    }

    if (stationToStationHasResults) {
        return (
            <>
                <Portal
                    id="popularBusRoutes"
                    type="div"
                >
                    <BestPriceOnDateRouteList
                        bestPriceOnDateData={(window.bestPriceOnDateData as Record<number, BestPriceRouteType[]>)?.[1] || []}
                    />
                </Portal>
                <Portal
                    id="popularTrainRoutes"
                    type="div"
                >
                    <BestPriceOnDateRouteList
                        bestPriceOnDateData={(window.bestPriceOnDateData as Record<number, BestPriceRouteType[]>)?.[3] || []}
                    />
                </Portal>
                {/* Always render overnight buses section - let React handle it */}
                <Portal
                    id="overnightPopularBusRoutes"
                    type="div"
                >
                    <BestPriceOnDateRouteList
                        bestPriceOnDateData={(window.bestPriceOnDateDataOverNightTrips as Record<number, BestPriceRouteType[]>)?.[1] || (window.bestPriceOnDateData as Record<number, BestPriceRouteType[]>)?.[1]?.filter(route => route.isOvernightTrip) || []}
                    />
                </Portal>
                {/* Mount datepicker for all gb--route-datepicker elements after portals mount */}
                <DelayedPortalMap className="gb--route-datepicker">
                    <StationToStationDatePicker
                        locale={locale}
                        transportType={transportType}
                    />
                </DelayedPortalMap>
                <Portal id="review-info">
                    <GenericPopover
                        trigger={trigger()}
                        overlay={overlayContent}
                        id="review-info"
                    />
                </Portal>
            </>
        )
    }

    if (portToPortHasResults) {
        // Normalize ferry data: handle both flat array and nested (like StationToStation or TrainToTrain pages) object formats
        const ferryData = window.bestPriceOnDateData as BestPriceRouteType[] | Record<number, BestPriceRouteType[]>
        const normalizedFerryData = Array.isArray(ferryData)
            ? ferryData
            : Object.values(ferryData).flat()

        return (
            <>
                <Portal
                    id="gb--best-price-on-date-ferry"
                    type="div"
                >
                    <BestPriceOnDateRouteList
                        bestPriceOnDateData={normalizedFerryData}
                    />
                </Portal>
                <Portal id="review-info">
                    <GenericPopover
                        trigger={trigger()}
                        overlay={overlayContent}
                        id="review-info"
                    />
                </Portal>
            </>
        )
    }

    return (
        <>
            <PortalMap className="result-tabs">
                <BestPriceTabs/>
            </PortalMap>
            <PortalMap className="gb--departure gb--truncate">
                <DepartureStationPopover/>
            </PortalMap>

            <PortalMap className="gb--bus-change">
                <StsBusChangeContainer/>
            </PortalMap>

            <PortalMap className="gb--route-ticket_type">
                <MTicketPopover/>
            </PortalMap>

            <PortalMap className="gb--route-price">
                <PricePopover/>
            </PortalMap>

            <PortalMap className="company-rating__score">
                <StsCompanyRating/>
            </PortalMap>

            <Portal id="review-info">
                <GenericPopover
                    trigger={trigger()}
                    overlay={overlayContent}
                    id="review-info"
                />
            </Portal>
        </>
    )
}

// DelayedPortalMap component that waits for other portals to mount
function DelayedPortalMap({className, children}: {className: string, children: React.ReactNode}) {
    const [shouldRender, setShouldRender] = React.useState(false);

    React.useEffect(() => {
        // Wait for other portals to mount, then render the PortalMap
        const timer = setTimeout(() => {
            setShouldRender(true);
        }, 200);
        return () => clearTimeout(timer);
    }, []);

    if (!shouldRender) {
        return null;
    }

    return (
        <PortalMap className={className}>
            {children}
        </PortalMap>
    );
}

export default BestPriceOnDatePortal